import { type DialogProps, Typography } from '@mui/material';
import { Dialog, DialogActionsWithButtons, DialogContent } from '@side/pantry';
import { useEffect } from 'react';
import {
  type FieldValues,
  FormProvider,
  type UseFormReturn,
} from 'react-hook-form';
import { useUnsavedChangesContext } from '@/components/UnsavedChanges';
import {
  TrackContactModalEvent,
  useTrackContactModalAnalytics,
} from '$analytics/useTrackAnalytics/domains/contactModal';
import { ContactModalState } from './ContactFormModal.helpers';

const dialogTitleByState: Record<ContactModalState, string> = {
  [ContactModalState.CLOSED]: '',
  [ContactModalState.CONTACT_INCOMPLETE]: 'More Information Required',
  [ContactModalState.ADDING_CONTACT]: 'Enter Contact Manually',
  [ContactModalState.EDITING_CONTACT]: 'Edit Contact',
  [ContactModalState.EXISTING_CONTACT_FOUND]: 'Existing Contact Found',
};

const submitButtonTextByState: Record<ContactModalState, string> = {
  [ContactModalState.CLOSED]: '',
  [ContactModalState.CONTACT_INCOMPLETE]: 'Save',
  [ContactModalState.ADDING_CONTACT]: 'Save',
  [ContactModalState.EDITING_CONTACT]: 'Update',
  [ContactModalState.EXISTING_CONTACT_FOUND]: 'Use Existing Contact',
};

const submitButtonLoadingTextByState: Record<ContactModalState, string> = {
  [ContactModalState.CLOSED]: '',
  [ContactModalState.CONTACT_INCOMPLETE]: 'Saving',
  [ContactModalState.ADDING_CONTACT]: 'Saving',
  [ContactModalState.EDITING_CONTACT]: 'Updating',
  [ContactModalState.EXISTING_CONTACT_FOUND]: '',
};

interface Props<T extends FieldValues>
  extends Omit<DialogProps, 'open' | 'onClose' | 'onSubmit'> {
  contactType: string;
  form: UseFormReturn<T, unknown>;
  onCancel?: () => void;
  onClose?: () => void;
  onSubmit: (values: T) => void;
  state: ContactModalState;
}

export function ContactFormModal<T extends FieldValues>({
  children,
  contactType,
  form,
  onCancel,
  onClose,
  onSubmit,
  state,
  ...dialogProps
}: Props<T>) {
  const trackEvent = useTrackContactModalAnalytics();

  const open = state !== ContactModalState.CLOSED;

  const {
    formState: { isSubmitting, isValid, isDirty },
  } = form;

  const { setIsPageDirty } = useUnsavedChangesContext();

  useEffect(() => {
    setIsPageDirty(open && isDirty);
  }, [setIsPageDirty, isDirty, open]);

  const dialogTitle = dialogTitleByState[state];
  const dialogCopyByState = {
    [ContactModalState.CLOSED]: '',
    [ContactModalState.CONTACT_INCOMPLETE]: `Add additional information for this ${contactType} contact`,
    [ContactModalState.ADDING_CONTACT]: `Add a new ${contactType} contact`,
    [ContactModalState.EDITING_CONTACT]: `Update ${contactType} contact information`,
    [ContactModalState.EXISTING_CONTACT_FOUND]: `By selecting the existing contact, we'll auto-populate the contact information for the ${contactType}`,
  };

  const handleClose = (target: string) => {
    trackEvent(TrackContactModalEvent.Close, {
      eventTarget: target,
      eventContainer:
        state === ContactModalState.EXISTING_CONTACT_FOUND
          ? 'manual_contact_modal_existing'
          : 'manual_contact_modal',
    });

    onClose?.();
  };

  return (
    <Dialog
      maxWidth="md"
      open={open}
      onClose={(_, reason) => handleClose(reason)}
      {...dialogProps}
    >
      <DialogContent
        title={dialogTitle}
        onClose={() => handleClose('close_button')}
        sx={{
          pb: 0,
          overflowX: 'hidden',
          maxHeight: { xs: 'none', md: '430px' },
        }}
      >
        <Typography sx={{ mb: 2 }}>{dialogCopyByState[state]}</Typography>
        <FormProvider {...form}>
          <form
            onSubmit={(e) => {
              void form.handleSubmit(onSubmit)(e);
            }}
          >
            {children}
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActionsWithButtons
        CancelButtonProps={{
          buttonText:
            state === ContactModalState.EXISTING_CONTACT_FOUND
              ? 'Back To Editing'
              : 'Cancel',
          onClick: () => {
            if (state !== ContactModalState.EXISTING_CONTACT_FOUND) {
              trackEvent(TrackContactModalEvent.Close, {
                eventTarget: 'cancel_button',
                eventContainer: 'manual_contact_modal',
              });
            }

            onCancel?.();
          },
        }}
        SubmitButtonProps={{
          defaultText: submitButtonTextByState[state],
          disabled: !isValid,
          loading: isSubmitting,
          loadingText: submitButtonLoadingTextByState[state],
          onClick: () => {
            void form.handleSubmit(onSubmit)();
          },
        }}
      />
    </Dialog>
  );
}
